import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import Restaurant from '../../../app/data/models/restaurant';
import api from '../../../app/api/api';
import type { RootState } from '../../../app/store/configureStore';
import CurrentPageItem from '../../../app/data/models/common/currentPageItem';

interface SelectedRestaurant {
  id: string;
  name: string;
  logoUrl?: string;
}

export interface RestaurantsState {
  restaurantsLoaded: boolean;
  status: string;
  nextPageId: string | undefined;
  selectedRestaurant?: SelectedRestaurant;
}

const restaurantsAdapter = createEntityAdapter<Restaurant>();

export const fetchRestaurantListAsync = createAsyncThunk<
  CurrentPageItem<Restaurant>,
  string | undefined
>('restaurants/fetchRestaurantListAsync', async (nextPageId, thunkApi) => {
  try {
    return await api.Restaurant.list(nextPageId);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkApi.rejectWithValue({ error: error.response?.data });
    }
    return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const fetchRestaurantAsync = createAsyncThunk<Restaurant, string>(
  'restaurants/fetchRestaurantAsync',
  async (restaurantId, thunkApi) => {
    try {
      return await api.Restaurant.details(restaurantId);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkApi.rejectWithValue({ error: error.response?.data });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const fetchRestaurantConfigAsync = createAsyncThunk<Restaurant, string>(
  'restaurants/fetchRestaurantConfigAsync',
  async (restaurantId, thunkApi) => {
    try {
      return await api.Restaurant.config(restaurantId);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkApi.rejectWithValue({ error: error.response?.data });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const deleteRestaurantAsync = createAsyncThunk<void, string>(
  'restaurant/deleteRestaurantAsync',
  async (restaurantId: string, thunkAPI) => {
    try {
      await api.Restaurant.delete(restaurantId);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkAPI.rejectWithValue({ error: error.response?.data });
      }
      return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const restaurantsSlice = createSlice({
  name: 'restaurants',
  initialState: restaurantsAdapter.getInitialState<RestaurantsState>({
    restaurantsLoaded: false,
    status: 'idle',
    nextPageId: undefined,
    selectedRestaurant: undefined,
  }),
  reducers: {
    resetRestaurantState: (state) => {
      restaurantsAdapter.removeAll(state);
      state.restaurantsLoaded = false;
      state.nextPageId = undefined;
    },
    setRestaurantsLoaded: (state, action) => {
      state.restaurantsLoaded = action.payload;
    },
    setSelectedRestaurant: (
      state,
      action: PayloadAction<SelectedRestaurant>
    ) => {
      state.selectedRestaurant = action.payload;
    },
    resetSelectedRestaurant: (state) => {
      state.selectedRestaurant = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRestaurantListAsync.pending, (state) => {
      state.status = 'pendingFetchRestaurantList';
    });
    builder.addCase(fetchRestaurantListAsync.fulfilled, (state, action) => {
      restaurantsAdapter.setAll(state, action.payload.data);
      state.restaurantsLoaded = true;
      state.status = 'idle';
      state.nextPageId = action.payload.nextPageId;
    });
    builder.addCase(fetchRestaurantListAsync.rejected, (state) => {
      state.status = 'idle';
    });
    builder.addCase(fetchRestaurantAsync.pending, (state) => {
      state.status = 'pendingFetchRestaurantList';
    });
    builder.addCase(fetchRestaurantAsync.fulfilled, (state, action) => {
      restaurantsAdapter.upsertOne(state, action.payload);
      state.status = 'idle';
    });
    builder.addCase(fetchRestaurantAsync.rejected, (state) => {
      state.status = 'idle';
    });
    builder.addCase(deleteRestaurantAsync.pending, (state) => {
      state.status = 'pendingDelete';
    });
    builder.addCase(deleteRestaurantAsync.fulfilled, (state) => {
      state.status = 'idle';
    });
    builder.addCase(deleteRestaurantAsync.rejected, (state) => {
      state.status = 'idle';
    });
  },
});

export const {
  resetRestaurantState,
  setRestaurantsLoaded,
  setSelectedRestaurant,
  resetSelectedRestaurant,
} = restaurantsSlice.actions;

export const getRestaurantSelectors = () =>
  restaurantsAdapter.getSelectors<RootState>((state) => state.restaurants);
